'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomMacGeneratorContent } from '../../data/random-mac-generator-content';
import Link from 'next/link';

type MACFormat = 'hyphen' | 'colon' | 'dot' | 'none';
type MACCase = 'lower' | 'upper';

export default function RandomMACGenerator() {
    const [macAddress, setMacAddress] = useState<string>('');
    const [format, setFormat] = useState<MACFormat>('colon');
    const [letterCase, setLetterCase] = useState<MACCase>('lower');
    const [count, setCount] = useState<number>(1);
    const [copied, setCopied] = useState<boolean>(false);
    const { toast } = useToast();

    const generateRandomHex = useCallback(() => {
        return Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
    }, []);

    const generateMAC = useCallback(() => {
        const octets = Array(6).fill(0).map(() => generateRandomHex());
        return octets;
    }, [generateRandomHex]);

    const formatMAC = useCallback((octets: string[]) => {
        let separator = '';
        switch (format) {
            case 'hyphen': separator = '-'; break;
            case 'colon': separator = ':'; break;
            case 'dot': separator = '.'; break;
            case 'none': separator = ''; break;
        }

        let formatted = octets.join(separator);
        return letterCase === 'upper' ? formatted.toUpperCase() : formatted.toLowerCase();
    }, [format, letterCase]);

    const generateNewMAC = useCallback(() => {
        const octets = generateMAC();
        setMacAddress(formatMAC(octets));
        setCopied(false);
    }, [generateMAC, formatMAC]);

    const generateMultipleMACs = useCallback(() => {
        const results = [];
        for (let i = 0; i < count; i++) {
            const octets = generateMAC();
            results.push(formatMAC(octets));
        }
        return results.join('\n');
    }, [count, generateMAC, formatMAC]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'MAC address has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    }, [toast]);

    useEffect(() => {
        generateNewMAC();
    }, [generateNewMAC]);

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Random MAC Generator">
                Generate random MAC addresses with our free online tool. Create hardware addresses for network testing, device simulation, and development.
            </ToolPageIntro>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>MAC Address</CardTitle>
                            <CardDescription>Generate random MAC addresses with different formats</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center space-x-2">
                                <Input
                                    value={macAddress}
                                    readOnly
                                    className="text-lg font-mono flex-1"
                                />
                                <Button
                                    size="icon"
                                    variant="outline"
                                    onClick={() => copyToClipboard(macAddress)}
                                    aria-label="Copy to clipboard"
                                >
                                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                </Button>
                                <Button onClick={generateNewMAC} variant="outline">
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate
                                </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="format" className="block mb-2">Format</Label>
                                    <select
                                        id="format"
                                        value={format}
                                        onChange={(e) => setFormat(e.target.value as MACFormat)}
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <option value="colon">Colon (00:1A:2B:3C:4D:5E)</option>
                                        <option value="hyphen">Hyphen (00-1A-2B-3C-4D-5E)</option>
                                        <option value="dot">Dot (001A.2B3C.4D5E)</option>
                                        <option value="none">No Separator (001A2B3C4D5E)</option>
                                    </select>
                                </div>
                                <div>
                                    <Label htmlFor="case" className="block mb-2">Case</Label>
                                    <select
                                        id="case"
                                        value={letterCase}
                                        onChange={(e) => setLetterCase(e.target.value as MACCase)}
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <option value="lower">Lowercase (a1:b2:c3:...)</option>
                                        <option value="upper">Uppercase (A1:B2:C3:...)</option>
                                    </select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Multiple MAC Addresses</CardTitle>
                            <CardDescription>Generate multiple MAC addresses at once</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <div className="flex-1">
                                        <Label htmlFor="count" className="block mb-2">Number of MAC Addresses</Label>
                                        <Input
                                            id="count"
                                            type="number"
                                            min="1"
                                            max="100"
                                            value={count}
                                            onChange={(e) => setCount(Math.min(Math.max(1, parseInt(e.target.value) || 1), 100))}
                                            className="w-24"
                                        />
                                    </div>
                                    <Button
                                        onClick={() => copyToClipboard(generateMultipleMACs())}
                                        className="mt-6"
                                        variant="outline"
                                    >
                                        <Copy className="mr-2 h-4 w-4" />
                                        Copy All
                                    </Button>
                                </div>
                                <div className="bg-muted p-4 rounded-md">
                                    <pre className="text-sm overflow-auto max-h-60">
                                        {generateMultipleMACs()}
                                    </pre>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>About MAC Addresses</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p>
                                A MAC (Media Access Control) address is a unique identifier assigned to a network interface controller (NIC) for use as a network address in communications within a network segment.
                            </p>
                            <div className="space-y-2">
                                <h3 className="font-semibold">Common Formats:</h3>
                                <ul className="list-disc pl-5 space-y-1 text-sm">
                                    <li><span className="font-mono">00:1A:2B:3C:4D:5E</span> (Colon)</li>
                                    <li><span className="font-mono">00-1A-2B-3C-4D-5E</span> (Hyphen)</li>
                                    <li><span className="font-mono">001A.2B3C.4D5E</span> (Dot)</li>
                                    <li><span className="font-mono">001A2B3C4D5E</span> (No separator)</li>
                                </ul>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Note: This tool generates random MAC addresses for testing and development purposes only.
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomMacGeneratorContent.title}
                description={randomMacGeneratorContent.description}
                content={randomMacGeneratorContent.content}
                faqs={randomMacGeneratorContent.faqs}
                relatedTools={randomMacGeneratorContent.relatedTools}
            />
        </div>
    );
}
